// The links that follow one request, or one sign-in session, across the two
// logs: the Request log (/admin/request-log, super admins with
// SYS_REQUEST_LOG) and the Audit log. Each is a plain URL that the page it
// opens reads back exactly (request-log-view.js; filters-model.js), so a
// link reproduces the view and can be bookmarked or shared.

const DAY = 864e5

// content's audit-request maxList: the Request log reads at most 31 days at
// once.
export const REQUEST_LOG_MAX_MS = 31 * DAY
// "Around this moment": 10 seconds either side.
export const AROUND_MS = 10 * 1000

export const REQUEST_LOG = '/admin/request-log'

const auditLog = sys => `${sys ? '/admin' : '/org'}/audit-log`
const iso = ms => new Date(ms).toISOString()
const timeOf = v => {
  const t = v ? Date.parse(v) : NaN
  return Number.isNaN(t) ? null : t
}
const href = (path, entries) => {
  const s = new URLSearchParams()
  entries.forEach(([k, v]) => v && s.set(k, v))
  const q = s.toString()
  return q ? `${path}?${q}` : path
}

// Every request ms either side of at, across every service and person.
export const aroundHref = (at, ms = AROUND_MS) => {
  const t = timeOf(at)
  return t === null ? null : href(REQUEST_LOG, [['from', iso(t - ms)], ['to', iso(t + ms)]])
}

// Every request in a sign-in session. One from the last 30 days opens the
// Request log's own session window (the last 31 days, read fresh); an
// older one, the 31 days centred on it. A session that ran longer than that
// is told on the page, which offers the 31 days before or after.
export const sessionRequestsHref = (sessionID, at, now = new Date()) => {
  if (!sessionID) return null
  const t = timeOf(at)
  if (t === null || t >= now.getTime() - 30 * DAY) return href(REQUEST_LOG, [['session', sessionID]])
  const half = REQUEST_LOG_MAX_MS / 2
  return href(REQUEST_LOG, [['session', sessionID], ['from', iso(t - half)], ['to', iso(t + half)]])
}

// "Every request from this address" and "every request in this sign-in
// attempt": a day either side of the request (content reads an attempt's
// summary over the same window, audit-request traceWindow).
export const ADDRESS_MS = DAY
export const TRACE_MS = DAY

const dayAround = (t, ms) => [['from', iso(t - ms)], ['to', iso(t + ms)]]

// Every request from one client address, a day either side of at (the
// list's ip filter; content takes an address or a CIDR range).
export const addressRequestsHref = (ip, at) => {
  const t = timeOf(at)
  if (!ip || t === null) return null
  return href(REQUEST_LOG, [['ip', ip], ...dayAround(t, ADDRESS_MS)])
}

// Every request in one flow (a sign-in attempt: core's trace key, never a
// secret), a day either side of at.
export const traceRequestsHref = (traceKey, at) => {
  const t = timeOf(at)
  if (!traceKey || t === null) return null
  return href(REQUEST_LOG, [['trace', traceKey], ...dayAround(t, TRACE_MS)])
}

// What one request changed: the audit log for it, a day either side of when
// it ran (an entry is stamped while its request runs).
export const requestChangesHref = (requestID, at, { sys = true } = {}) => {
  if (!requestID) return null
  const t = timeOf(at)
  const window = t === null ? [] : [['range', 'custom'], ['from', iso(t - DAY)], ['to', iso(t + DAY)]]
  return href(auditLog(sys), [['request', requestID], ...window])
}

// What one session changed: the audit log for it over the log's longest
// preset (the last 12 months).
export const sessionChangesHref = (sessionID, { sys = true } = {}) =>
  sessionID ? href(auditLog(sys), [['session', sessionID], ['range', '365d']]) : null
